import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, Navigate, Link, useLocation } from 'react-router-dom';
import { Building, LogOut, ChevronLeft, ChevronRight, ChevronDown, Menu, X, Building2, Search } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@pms/shared';
import { getNavSections, type NavItem, type NavSection } from '@/config/navigation';
import { cn } from '@/lib/utils';
import { useOnboardingStatus } from '@/hooks/api/useOnboarding';
import { PropertyProvider, usePropertyContext } from '@/contexts/PropertyContext';
import { PropertySelector } from '@/components/PropertySelector';
import { PropertySwitcherPopover } from '@/components/layout/PropertySwitcherPopover';
import { SearchCommandPalette } from '@/components/SearchCommandPalette';
import { BestAccoAssistant } from '@/components/assistant/BestAccoAssistant';

const SIDEBAR_STORAGE_KEY = 'pms_sidebar_collapsed';

function getRoleLabel(role: UserRole): string {
  const labels: Record<UserRole, string> = {
    // super_admin is never surfaced in the tenant-facing UI; neutral fallback only.
    [UserRole.SUPER_ADMIN]: 'Administrator',
    [UserRole.ADMIN]: 'Administrator',
    [UserRole.REVENUE_MANAGER]: 'Revenue Manager',
    [UserRole.OPERATIONS]: 'Operations',
    [UserRole.OWNER]: 'Owner',
  };
  return labels[role] ?? role;
}

// ─── SidebarNavItem ────────────────────────────────────────────────────────────

function SidebarNavItem({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      aria-label={item.label}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
          'min-h-[40px]',
          collapsed
            ? 'justify-center p-2.5'
            : 'gap-3 px-3 py-2',
          isActive
            ? 'bg-primary-50 text-primary-700 border-l-2 border-primary-600'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
          item.readOnly ? 'opacity-60 cursor-default' : '',
          'text-sm font-medium',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            size={18}
            aria-hidden="true"
            className={cn(
              'shrink-0',
              isActive ? 'text-primary-600' : 'text-gray-400',
            )}
          />
          {!collapsed && <span>{item.label}</span>}
        </>
      )}
    </NavLink>
  );
}

// ─── SidebarContents ───────────────────────────────────────────────────────────
// Shared between desktop sidebar and mobile overlay — always "expanded" on mobile.

function SidebarContents({
  collapsed,
  onToggleCollapse,
  navSections,
  initials,
  displayName,
  tenantName,
  role,
  onLogout,
  onNavigate,
  showCollapseToggle,
}: {
  collapsed: boolean;
  onToggleCollapse: () => void;
  navSections: NavSection[];
  initials: string;
  displayName: string;
  tenantName: string;
  role: UserRole;
  onLogout: () => void;
  onNavigate?: () => void;
  showCollapseToggle: boolean;
}) {
  const { selectedProperty } = usePropertyContext();
  const roleLabel = getRoleLabel(role);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (title: string) => {
    setCollapsedSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  return (
    <>
      {/* Header — tenant/org name */}
      <div className="px-4 py-4 border-b border-gray-100 shrink-0">
        <Link
          to="/dashboard"
          className="flex items-center gap-3 rounded-lg transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          aria-label="Go to dashboard"
        >
          <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center shrink-0">
            <Building size={16} className="text-white" aria-hidden="true" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{tenantName}</p>
              <p className="text-xs text-gray-500 truncate">Property Management</p>
            </div>
          )}
        </Link>
      </div>

      {/* Group property switcher (searchable / paginated + portfolio) */}
      <PropertySwitcherPopover collapsed={collapsed} />

      {/* Nav sections */}
      <nav
        aria-label="Main navigation"
        className="flex-1 overflow-y-auto py-2 px-2"
      >
        {navSections.map((section, sectionIndex) => {
          // Hide non-dashboard sections when no property is selected
          if (!selectedProperty && section.title !== '') return null;

          const hasTitle = section.title.length > 0;
          const isCollapsedSection = hasTitle && collapsedSections[section.title] === true;
          return (
            <div key={section.title || `section-${sectionIndex}`} className="mb-1">
              {/* Section header — skip for untitled sections (e.g. standalone Dashboard) */}
              {hasTitle && !collapsed && (
                <button
                  type="button"
                  onClick={() => toggleSection(section.title)}
                  className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
                  aria-expanded={!isCollapsedSection}
                >
                  <span>{section.title}</span>
                  <ChevronDown
                    size={12}
                    className={cn(
                      'transition-transform duration-200',
                      isCollapsedSection ? '-rotate-90' : 'rotate-0',
                    )}
                    aria-hidden="true"
                  />
                </button>
              )}
              {/* Section items */}
              {!isCollapsedSection && (
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <SidebarNavItem
                      key={item.path}
                      item={item}
                      collapsed={collapsed}
                      {...(onNavigate !== undefined ? { onNavigate } : {})}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Prompt to select a property when none is active */}
        {!selectedProperty && !collapsed && (
          <div className="mx-2 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs font-medium text-amber-800">
              Select a property to access all features
            </p>
          </div>
        )}
      </nav>

      {/* User section */}
      <div className="shrink-0 border-t border-gray-100 p-3">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-full bg-primary-600 text-white text-xs font-semibold flex items-center justify-center shrink-0"
            aria-hidden="true"
          >
            {initials}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
              <p className="text-xs text-gray-500 truncate">{roleLabel}</p>
            </div>
          )}
          {!collapsed && (
            <button
              type="button"
              onClick={onLogout}
              title="Sign out"
              aria-label="Sign out"
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            >
              <LogOut size={16} aria-hidden="true" />
            </button>
          )}
        </div>
        {collapsed && (
          <button
            type="button"
            onClick={onLogout}
            title="Sign out"
            aria-label="Sign out"
            className="mt-2 w-full flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            <LogOut size={16} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Collapse toggle — desktop only */}
      {showCollapseToggle && (
        <div className="shrink-0 border-t border-gray-100 p-2">
          <button
            type="button"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="w-full flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            {!collapsed && <span className="ml-2 text-xs">Collapse</span>}
          </button>
        </div>
      )}
    </>
  );
}

// ─── PropertyGatedOutlet ──────────────────────────────────────────────────────
// Shows the PropertySelector landing page when multiple properties exist but none
// is selected. Otherwise renders the normal <Outlet />.

function PropertyGatedOutlet() {
  const { properties, selectedProperty, isLoading, isError } = usePropertyContext();
  const location = useLocation();

  // Still loading properties — show a centered spinner
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" role="status" aria-label="Loading" />
      </div>
    );
  }

  // These pages are accessible regardless of property selection
  const UNGATED_PATHS = ['/dashboard', '/properties', '/users', '/settings', '/owner-dashboard', '/email-logs', '/audit-log'];
  if (UNGATED_PATHS.includes(location.pathname)) {
    return <Outlet />;
  }

  // API error (502, network down) — show error, don't falsely say "no properties"
  if (isError && properties.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <span className="text-red-600 text-xl font-bold">!</span>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Connection Error</h2>
        <p className="text-gray-500 mb-4">Unable to load properties. Please check your connection and try again.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // No properties at all (new tenant) — prompt to create one
  if (properties.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Building2 size={48} className="text-gray-300 mb-4" aria-hidden="true" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">No Properties</h2>
        <p className="text-gray-500 mb-4">Create your first property to get started.</p>
        <Link
          to="/properties"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          Go to Properties
        </Link>
      </div>
    );
  }

  // Property not selected — show the selector
  if (!selectedProperty) {
    return <PropertySelector />;
  }

  return <Outlet />;
}

// ─── AppLayout ─────────────────────────────────────────────────────────────────

export function AppLayout() {
  const { user, logout } = useAuth();

  // super_admin has no tenant onboarding and 403s on every hotel endpoint —
  // they must never enter the hotel shell. Send them to the platform console.
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

  // Redirect to onboarding if not completed (skip the query entirely for
  // super_admin — the onboarding/status endpoint is tenant-scoped).
  const { data: onboardingStatus, isLoading: onboardingLoading } = useOnboardingStatus(!isSuperAdmin);

  // Collapsed state — desktop only, persisted in localStorage
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Mobile overlay open state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Global search state
  const [searchOpen, setSearchOpen] = useState(false);

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const toggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // Close mobile overlay on resize to desktop breakpoint
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setMobileOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Close mobile overlay on Escape
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobile();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mobileOpen, closeMobile]);

  const role = user?.role ?? UserRole.OWNER;
  const displayName = user?.email?.split('@')[0] ?? '';
  const initials = displayName ? displayName.slice(0, 2).toUpperCase() : '?';
  // Derive tenant name from email domain — replace when backend adds tenantName
  const tenantName =
    user?.email?.split('@')[1]?.split('.')[0]
      ? user.email.split('@')[1].split('.')[0].charAt(0).toUpperCase() +
        user.email.split('@')[1].split('.')[0].slice(1) + ' PMS'
      : 'BestAcco PMS';
  const navSections = getNavSections(role);

  // super_admin never belongs in the hotel shell — bounce to the platform console.
  if (isSuperAdmin) {
    return <Navigate to="/admin" replace />;
  }

  // Onboarding is a first-run helper, NOT a wall. Force it ONLY for a brand-new
  // tenant that has no property yet — the one case where the PMS has nothing to
  // show. Once a property exists (e.g. BestAcco provisioned/created it, or the
  // hotel made one), never force it again: the user lands in the PMS and can
  // finish or skip onboarding at their own pace from the Home checklist. This
  // stops the "onboarding every login" trap for partially-set-up properties.
  const hasProperty = onboardingStatus?.checklist?.propertyCreated === true;
  if (
    !onboardingLoading &&
    onboardingStatus &&
    !onboardingStatus.completed &&
    !hasProperty
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  const sharedProps = {
    navSections,
    initials,
    displayName,
    tenantName,
    role,
    onLogout: logout,
    onToggleCollapse: toggleCollapse,
  };

  return (
    <PropertyProvider>
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* ── Desktop sidebar ──────────────────────────────────────────────────── */}
      <aside
        className={cn(
          'hidden md:flex flex-col bg-white border-r border-gray-200 shadow-sm shrink-0',
          'transition-all duration-200 ease-in-out',
          collapsed ? 'w-16' : 'w-64',
        )}
        aria-label="Sidebar navigation"
      >
        <SidebarContents
          {...sharedProps}
          collapsed={collapsed}
          showCollapseToggle={true}
        />
      </aside>

      {/* ── Mobile overlay ───────────────────────────────────────────────────── */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            aria-hidden="true"
            onClick={closeMobile}
          />
          {/* Sidebar panel */}
          <div
            className="fixed inset-y-0 left-0 w-72 max-w-[80vw] bg-white border-r border-gray-200 z-50 flex flex-col md:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            {/* Close button */}
            <button
              type="button"
              onClick={closeMobile}
              aria-label="Close navigation menu"
              className="absolute top-3 right-3 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            >
              <X size={18} aria-hidden="true" />
            </button>
            <SidebarContents
              {...sharedProps}
              collapsed={false}
              showCollapseToggle={false}
              onNavigate={closeMobile}
            />
          </div>
        </>
      )}

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <main
        id="main-content"
        className="flex-1 overflow-y-auto bg-gray-50"
        tabIndex={-1}
      >
        {/* Mobile header — visible only on small screens */}
        <div className="md:hidden flex items-center h-14 px-4 bg-white border-b border-gray-200 sticky top-0 z-10">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation menu"
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <Menu size={20} aria-hidden="true" />
          </button>
          <span className="ml-3 font-semibold text-gray-900 text-sm flex-1">{tenantName}</span>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <Search size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Desktop top bar */}
        <div className="hidden md:flex items-center h-12 px-6 bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search (Cmd+K)"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-gray-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <Search size={16} aria-hidden="true" />
            <span>Search...</span>
            <kbd className="ml-2 px-1.5 py-0.5 text-[10px] font-mono bg-white border border-gray-200 rounded">
              {navigator.platform?.includes('Mac') ? '\u2318K' : 'Ctrl+K'}
            </kbd>
          </button>
        </div>

        <div className="p-4 lg:p-6">
          <PropertyGatedOutlet />
        </div>
      </main>
    </div>

    {/* Global search command palette */}
    <SearchCommandPalette isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

    {/* Floating conversational assistant — available on every PMS page */}
    <BestAccoAssistant variant="pms" />
    </PropertyProvider>
  );
}
